import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from 'js-cookie';
import useAuth from "../../components/hooks/useAuth";
import { getLocations } from "../../services/location";
import { getStatus } from "../../services/VendorStatus";
import { validateMin } from "../../utils/validateMin";
import PleaseWaitPopup from "../PleaseWaitPopup";
import { useTheme } from "../../components/hooks/useTheme";
import { formatUSPhone, getUSPhoneDigits } from "../../utils/phoneFormatter";
import colorConfigs from "../../configs/colorConfigs";
import { Button } from "@mui/material";
import { post_Vendor } from "../../services/Vendors";
import useTenant from "../../components/hooks/useTenant";
import EmailValidatedComponent from "../../components/EmailValidatedComponent";

export const AddVendor = () => {
    const navigate = useNavigate();
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { themeStyles } = useTheme();
    const { handleTenantError }: any = useTenant();
    const [locationOptions, setLocationOptions] = useState<any[]>([]);
    const [locationId, setLocationId] = useState<number>(0);
    const [statusOptions, setStatusOptions] = useState<any[]>([]);
    const [statusId, setStatusId] = useState<number>(0);
    const [vendorName, setVendorName] = useState<string>("");
    const [address1, setAddress1] = useState<string>("");
    const [address2, setAddress2] = useState<string>("");
    const [city, setCity] = useState<string>("");
    const [state, setState] = useState<string>("");
    const [zipCode, setZipCode] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false);
    const [fax, setFax] = useState<string>("");
    const [vendorTaxID, setVendorTaxID] = useState<string>("");
    const [vendorTerms, setVendorTerms] = useState<string>("");
    const [county, setCounty] = useState<string>("");
    const [country, setCountry] = useState<string>("");
    const [commission, setCommission] = useState<number>(0);
    const [rent, setRent] = useState<number>(0);
    const [contactPerson1, setContactPerson1] = useState<string>("");
    const [contactPerson1Phone, setContactPerson1Phone] = useState<string>("");
    const [isContact1PhoneValid, setIsContact1PhoneValid] = useState<boolean>(false);
    const [contactPerson2, setContactPerson2] = useState<string>("");
    const [contactPerson2Phone, setContactPerson2Phone] = useState<string>("");
    const [isContact2PhoneValid, setIsContact2PhoneValid] = useState<boolean>(false);
    const [website, setWebsite] = useState<string>("");
    const [minimumOrder, setMinimumOrder] = useState<number>(0);
    const [defaultOrderingMode, setDefaultOrderingMode] = useState<string>("");
    const [defaultBillableDepartment, setDefaultBillableDepartment] = useState<string>("");
    const [defaultPODelivery, setDefaultPODelivery] = useState<string>("");
    const [deliveryTime, setDeliveryTime] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);

    useEffect(() => {
        getLocations(tenantId)
            .then((data) => {
                const locations = data?.map((loc: any) => ({
                    value: loc?.id,
                    label: loc?.locationName
                }));

                setLocationOptions(locations);

                const savedTab = JSON.parse(
                    sessionStorage.getItem("vendorsTab") || "{}"
                );

                if (savedTab.activeIndex && savedTab.activeIndex !== 0) {
                    const selectedLocation = locations.find(
                        (loc: any) => loc.value === savedTab.activeIndex
                    );

                    if (selectedLocation) {
                        setLocationId(selectedLocation.value);
                    } else {
                        setLocationId(locations[0]?.value || 0);
                    }
                } else {
                    setLocationId(locations[0]?.value || 0);
                }

                handleTenantError(null);
            })
            .catch((error) => {
                handleTenantError(error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        getStatus("Vendor", tenantId)
            .then((data) => {
                setStatusOptions(() => data?.map((status: any) => {
                    return {
                        value: status?.id,
                        label: status?.statusName
                    }
                }))

                setStatusId(data[0]?.id);
                handleTenantError(null);
            })
            .catch((error) => {
                handleTenantError(error);
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (phone.trim() !== "") {
            timer = setTimeout(() => {
                const valid = /^\d{10}$/.test(phone);
                setIsPhoneValid(valid);
            }, 500);
        } else {
            setIsPhoneValid(true);
        }

        return () => clearTimeout(timer);
    }, [phone]);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (contactPerson1Phone.trim() !== "") {
            timer = setTimeout(() => {
                const valid = /^\d{10}$/.test(contactPerson1Phone);
                setIsContact1PhoneValid(valid);
            }, 500);
        } else {
            setIsContact1PhoneValid(true);
        }

        return () => clearTimeout(timer);
    }, [contactPerson1Phone]);


    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (contactPerson2Phone.trim() !== "") {
            timer = setTimeout(() => {
                const valid = /^\d{10}$/.test(contactPerson2Phone);
                setIsContact2PhoneValid(valid);
            }, 500);
        } else {
            setIsContact2PhoneValid(true);
        }

        return () => clearTimeout(timer);
    }, [contactPerson2Phone]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWaitPopup(true);

        await post_Vendor({
            locationId,
            vendorName,
            statusId,
            address1,
            address2,
            city,
            state,
            zipCode,
            phone,
            fax,
            vendorTaxID,
            vendorTerms,
            county,
            country,
            email,
            commission,
            rent,
            contactPerson1,
            contactPerson1Phone,
            contactPerson2,
            contactPerson2Phone,
            website,
            deliveryTime,
            minimumOrder,
            defaultOrderingMode,
            defaultBillableDepartment,
            defaultPODelivery,
            description,
            createdAt: new Date().toLocaleString(),
            createdBy: auth?.givenname,
        }, auth?.id, tenantId)
            .then(() => {
                setPleaseWaitPopup(false);
                navigate(-1);
                handleTenantError(null);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                handleTenantError(error);
            });
    }

    return (
        <>
            <div className={`px-[24px] py-[20px] min-h-screen h-full ${themeStyles.main.tailwindSectionBg}`}>
                <div className={`w-full ${themeStyles.main.tailwindMainBg} rounded-lg p-[10px] pb-[40px] flex flex-col gap-2.51`}>
                    <div className="w-full mt-2">
                        <div className={`w-full pb-4`}>

                            <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                <div className="flex items-center gap-1.5">
                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Add Vendor</h2>
                                </div>
                            </div>
                            <div className="flex">
                                <div className="mt-2 p-2 w-[70%]">
                                    <form onSubmit={(e) => handleSubmit(e)}>
                                        <div className="w-full px-2">
                                            <div className="w-full px-2">
                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Vendor Name<span className="text-red-500">*</span></label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={vendorName}
                                                            placeholder="Vendor Name"
                                                            onChange={(e) => setVendorName(e.target.value)}
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Phone Number<span className="text-red-500">*</span></label>
                                                        <input
                                                            type="text"
                                                            placeholder="Phone Number"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            required
                                                            value={formatUSPhone(phone)}
                                                            pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                                            title="Please Enter a 10 digits numbers."
                                                            onChange={(e) => {
                                                                const digits = getUSPhoneDigits(e.target.value);
                                                                setPhone(digits);
                                                            }}
                                                        />
                                                        {!isPhoneValid && (
                                                            <div className="flex items-center gap-1 mt-2 text-xs text-red-500 ml-1">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 26 26"
                                                                    strokeWidth={2}
                                                                    stroke="currentColor"
                                                                    className="w-4 h-4 text-red-500"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M12 9v3m0 3h.01m-9 0a9 9 0 1118 0 9 9 0 01-18 0z"
                                                                    />
                                                                </svg>

                                                                <span>Please enter a valid 10-digit phone number</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                                            Email Address
                                                            <span className="text-red-500">*</span></label>
                                                        <EmailValidatedComponent
                                                            text={email}
                                                            setText={setEmail}
                                                            required={true}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Fax</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={fax}
                                                            placeholder="Fax"
                                                            onChange={(e) => setFax(e.target.value)}

                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Status<span className="text-red-500">*</span></label>
                                                        <select
                                                            value={statusId}
                                                            onChange={(e) => setStatusId(Number(e.target.value))}
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        >

                                                            {statusOptions.map((status: any, index: number) => (
                                                                <option key={index} value={status?.value} className={`${themeStyles.main.tailwindSectionBg}`}>
                                                                    {status?.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Location<span className="text-red-500">*</span></label>
                                                        <select
                                                            value={locationId}
                                                            onChange={(e) => setLocationId(Number(e.target.value))}
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        >

                                                            {locationOptions.map((loc: any, index: number) => (
                                                                <option key={index} value={loc?.value} className={`${themeStyles.main.tailwindSectionBg}`}>
                                                                    {loc?.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Address 1<span className="text-red-500">*</span></label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={address1}
                                                            placeholder="Address1"
                                                            onChange={(e) => setAddress1(e.target.value)}
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Address 2</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={address2}
                                                            onChange={(e) => setAddress2(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>City<span className="text-red-500">*</span></label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={city}
                                                            placeholder="City"
                                                            onChange={(e) => setCity(e.target.value)}
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>State<span className="text-red-500">*</span></label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={state}
                                                            placeholder="State"
                                                            onChange={(e) => setState(e.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Zip Code<span className="text-red-500">*</span></label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={zipCode}
                                                            placeholder="Zip Code"
                                                            inputMode="numeric"
                                                            onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ""))}
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Country<span className="text-red-500">*</span></label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={country}
                                                            placeholder="Country"
                                                            onChange={(e) => setCountry(e.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>County</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={county}
                                                            placeholder="County"
                                                            onChange={(e) => setCounty(e.target.value)}

                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Vendor Terms</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={vendorTerms}
                                                            placeholder="Vendor Terms"
                                                            onChange={(e) => setVendorTerms(e.target.value)}

                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Tax ID</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={vendorTaxID}
                                                            placeholder="Tax ID"
                                                            onChange={(e) => setVendorTaxID(e.target.value)}

                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Rent</label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step={0.01}
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={rent}
                                                            placeholder="Rent"
                                                            onChange={(e) => setRent(validateMin(e.target.value, 0))}

                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Contact Person 1</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={contactPerson1}
                                                            placeholder="Contact Person 1"
                                                            onChange={(e) => setContactPerson1(e.target.value)}

                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Contact Person 1 Phone</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={formatUSPhone(contactPerson1Phone)}
                                                            placeholder="Contact Person 1 Phone"
                                                            pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                                            title="Please Enter a 10 digits numbers."
                                                            onChange={(e) => {
                                                                const digits = getUSPhoneDigits(e.target.value);
                                                                setContactPerson1Phone(digits);
                                                            }}

                                                        />
                                                        {!isContact1PhoneValid && (
                                                            <div className="flex items-center gap-1 mt-2 text-xs text-red-500 ml-1">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 26 26"
                                                                    strokeWidth={2}
                                                                    stroke="currentColor"
                                                                    className="w-4 h-4 text-red-500"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M12 9v3m0 3h.01m-9 0a9 9 0 1118 0 9 9 0 01-18 0z"
                                                                    />
                                                                </svg>

                                                                <span>Please enter a valid 10-digit phone number</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Contact Person 2</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={contactPerson2}
                                                            placeholder="Contact Person 2"
                                                            onChange={(e) => setContactPerson2(e.target.value)}

                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Contact Person 2 Phone</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={formatUSPhone(contactPerson2Phone)}
                                                            placeholder="Contact Person 2 Phone"
                                                            pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                                            title="Please Enter a 10 digits numbers."
                                                            onChange={(e) => {
                                                                const digits = getUSPhoneDigits(e.target.value);
                                                                setContactPerson2Phone(digits);
                                                            }}

                                                        />
                                                        {!isContact2PhoneValid && (
                                                            <div className="flex items-center gap-1 mt-2 text-xs text-red-500 ml-1">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 26 26"
                                                                    strokeWidth={2}
                                                                    stroke="currentColor"
                                                                    className="w-4 h-4 text-red-500"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M12 9v3m0 3h.01m-9 0a9 9 0 1118 0 9 9 0 01-18 0z"
                                                                    />
                                                                </svg>

                                                                <span>Please enter a valid 10-digit phone number</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Minimum Order</label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={minimumOrder}
                                                            placeholder="Minimum Order"
                                                            onChange={(e) => setMinimumOrder(validateMin(e.target.value, 0))}

                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={website}
                                                            placeholder="Website"
                                                            onChange={(e) => setWebsite(e.target.value)}

                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Default Billable Department</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={defaultBillableDepartment}
                                                            placeholder="Default Billable Department"
                                                            onChange={(e) => setDefaultBillableDepartment(e.target.value)}

                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Default Ordering Mode</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={defaultOrderingMode}
                                                            placeholder="Default Ordering Mode"
                                                            onChange={(e) => setDefaultOrderingMode(e.target.value)}

                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Delivery Time</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={deliveryTime}
                                                            placeholder="Delivery Time"
                                                            onChange={(e) => setDeliveryTime(e.target.value)}

                                                        />
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Default PO Delivery</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={defaultPODelivery}
                                                            placeholder="Default PO Delivery"
                                                            onChange={(e) => setDefaultPODelivery(e.target.value)}

                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Commission</label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step={0.01}
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={commission}
                                                            placeholder="Commission"
                                                            onChange={(e) => setCommission(validateMin(e.target.value, 0))}

                                                        />
                                                    </div>

                                                </div>

                                                <div className="grid grid-cols-2 gap-6 mb-2">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Description</label>
                                                        <textarea
                                                            className={`w-full px-[8px] py-[8px]  border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            rows={4}
                                                            value={description}
                                                            onChange={(e) => setDescription(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex gap-4 mt-6">
                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.main.bg
                                                    }}
                                                    type="submit"
                                                >
                                                    Save
                                                </Button>

                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => navigate(-1)}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                </div>

                                <div className="mt-2 p-2 w-[30%]">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
        </>
    )
}